import {
    createContext,
    useContext,
    useReducer,
    useCallback,
    useState,
    useEffect,
    useRef,
} from "react";
import { withTranslation } from "react-i18next";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const ToastContext = createContext({
    addToast: () => {},
    removeToast: () => {},
    updateToast: () => {},
});

const reducer = (state, action) => {
    switch (action.type) {
        case "ADD_TOAST":
            return {
                ...state,
                toasts: [...state.toasts, action.payload],
            };
        case "REMOVE_TOAST":
            return {
                ...state,
                toasts: state.toasts.filter(
                    (toast) => toast.id !== action.payload,
                ),
            };
        case "UPDATE_TOAST":
            return {
                ...state,
                toasts: state.toasts.map((toast) =>
                    toast.id === action.payload.id
                        ? { ...toast, ...action.payload }
                        : toast,
                ),
            };
        default:
            return state;
    }
};

/**
 * ToastProvider component - provides toast notification functionality
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement} The rendered ToastProvider
 */
export const ToastProvider = withTranslation()(({ t, children }) => {
    const [state, dispatch] = useReducer(reducer, { toasts: [] });
    const [closingToasts, setClosingToasts] = useState({});
    const [timeUpdate, setTimeUpdate] = useState(Date.now());
    const recentToastsRef = useRef({});

    const iconClasses = {
        danger: "vnf-icons-icon-s-erreur-solid",
        info: "vnf-icons-icon-s-information-solid",
        success: "vnf-icons-icon-s-succes-solid",
        warning: "vnf-icons-icon-s-attention-solid",
    };

    /**
     * Remove a toast notification by ID
     *
     * @param {string} id - The ID of the toast to remove
     * @returns {void}
     */
    const removeToast = useCallback((id) => {
        dispatch({
            type: "REMOVE_TOAST",
            payload: id,
        });
    }, []);

    /**
     * Start the closing animation for a toast and then remove it
     *
     * @param {string} id - The ID of the toast to close
     */
    const closeToast = useCallback(
        (id) => {
            setClosingToasts((prev) => ({ ...prev, [id]: true }));
            setTimeout(() => removeToast(id), 250);
        },
        [removeToast],
    );

    /**
     * Format a timestamp to a relative "time ago" string
     *
     * @param {Date|number} timestamp - The timestamp to format
     * @returns {string} Formatted "time ago" string
     */
    const formatTimeAgo = useCallback(
        (timestamp) => {
            const timeMs =
                typeof timestamp === "number" ? timestamp : timestamp.getTime();
            const seconds = Math.floor((Date.now() - timeMs) / 1000);

            if (seconds < 5) {
                return t("time_just_now");
            } else if (seconds < 60) {
                return t("time_seconds_ago", { count: seconds });
            } else if (seconds < 3600) {
                const minutes = Math.floor(seconds / 60);
                return minutes === 1
                    ? t("time_minute_ago")
                    : t("time_minutes_ago", { count: minutes });
            } else if (seconds < 86400) {
                const hours = Math.floor(seconds / 3600);
                return hours === 1
                    ? t("time_hour_ago")
                    : t("time_hours_ago", { count: hours });
            } else {
                const days = Math.floor(seconds / 86400);
                return days === 1
                    ? t("time_day_ago")
                    : t("time_days_ago", { count: days });
            }
        },
        [t],
    );

    /**
     * Clean up old entries from the recent toasts tracking
     */
    const cleanupRecentToasts = useCallback(() => {
        const now = Date.now();
        const recentToasts = recentToastsRef.current;

        Object.keys(recentToasts).forEach((key) => {
            // Remove entries older than 1 second
            if (now - recentToasts[key] > 1000) {
                delete recentToasts[key];
            }
        });
    }, []);

    /**
     * Create a unique key for a toast based on its content and appearance
     *
     * @param {string} content - Toast content
     * @param {Object} options - Toast options
     * @returns {string} A string key representing the toast
     */
    const getToastKey = useCallback((content, options) => {
        return `${content}-${options.appearance || "default"}-${options.message || ""}`;
    }, []);

    /**
     * Add a new toast notification
     *
     * @param {string} content - The message to display
     * @param {Object} options - Configuration options
     * @param {string} options.appearance - The toast type ('success', 'danger', 'warning', 'info')
     * @param {boolean} options.autoDismiss - Whether to automatically dismiss the toast
     * @param {number} options.autoDismissTimeout - Time in milliseconds before auto-dismissing
     * @param {string} options.size - The size of the toast ('md' or 'xl')
     * @param {string} options.message - Additional message content (optional)
     * @param {boolean} options.showTime - Whether to show the time display (default: true)
     * @param {boolean} options.showCloseButton - Whether to show the close button (default: true)
     * @param {Array<Object>} options.buttons - Action buttons
     * @param {string} options.buttons[].text - Button text
     * @param {Function} options.buttons[].onClick - Button click handler
     * @param {boolean} options.buttons[].primary - Whether this is the primary button (default: false)
     * @returns {string} The ID of the created toast
     */
    const addToast = useCallback(
        (content, options = {}) => {
            cleanupRecentToasts();
            const toastKey = getToastKey(content, options);

            const now = Date.now();
            if (
                recentToastsRef.current[toastKey] &&
                now - recentToastsRef.current[toastKey] < 1000
            ) {
                return null;
            }

            recentToastsRef.current[toastKey] = now;

            const id = Math.random().toString(36).substr(2, 9);
            const timestamp = Date.now();

            if (options.appearance === "error") {
                options.appearance = "danger";
            }

            const toastOptions = {
                size: "md",
                showTime: false,
                showCloseButton: true,
                ...options,
            };

            dispatch({
                type: "ADD_TOAST",
                payload: {
                    id,
                    content,
                    timestamp,
                    ...toastOptions,
                },
            });

            if (toastOptions.autoDismiss) {
                setTimeout(() => {
                    removeToast(id);
                }, toastOptions.autoDismissTimeout || 4000);
            }

            return id;
        },
        [removeToast, cleanupRecentToasts, getToastKey],
    );

    /**
     * Update a toast notification by ID
     *
     * @param {string} id - The ID of the toast to update
     * @param {Object} options - Configuration options
     * @returns {void}
     */
    const updateToast = useCallback((id, options) => {
        dispatch({
            type: "UPDATE_TOAST",
            payload: { id, ...options },
        });
    }, []);

    // Auto dismiss effect for each toast
    useEffect(() => {
        state.toasts.forEach((toast) => {
            if (toast.autoDismiss && !closingToasts[toast.id]) {
                const timer = setTimeout(() => {
                    closeToast(toast.id);
                }, toast.autoDismissTimeout || 4000);

                return () => clearTimeout(timer);
            }
        });
    }, [state.toasts, closingToasts, closeToast]);

    useEffect(() => {
        if (state.toasts.length > 0) {
            const intervalId = setInterval(() => {
                setTimeUpdate(Date.now());
            }, 10000); // Update every 10 seconds

            return () => clearInterval(intervalId);
        }
    }, [state.toasts.length, timeUpdate]);

    return (
        <ToastContext.Provider value={{ addToast, removeToast, updateToast }}>
            {children}
            <div className="react-toast-notifications__container">
                {state.toasts.map((toast) => {
                    const {
                        id,
                        appearance,
                        content,
                        timestamp,
                        message,
                        size = "md",
                        autoDismiss,
                        autoDismissTimeout,
                        showTime = false,
                        showCloseButton = true,
                        buttons = [],
                    } = toast;

                    const isClosing = closingToasts[id] || false;
                    const timeAgo =
                        timestamp && showTime ? formatTimeAgo(timestamp) : null;

                    return (
                        <div
                            key={id}
                            className={`vnf-toast vnf-toast--${appearance} vnf-toast--${size} ${isClosing ? "close" : ""}`}
                        >
                            <div className="vnf-toast__header">
                                <i
                                    className={`vnf-toast__icon ${iconClasses[appearance] || ""}`}
                                ></i>
                                <p
                                    className={`vnf-toast__title vnf-toast__title--${size}`}
                                >
                                    {content}
                                </p>
                                {showCloseButton && (
                                    <button
                                        className="vnf-toast__close"
                                        onClick={() =>
                                            !isClosing && closeToast(id)
                                        }
                                    >
                                        <i className="vnf-icons-icon-m-croix-fermer-outline"></i>
                                        <span className="sr-only">
                                            {t("Close notification")}
                                        </span>
                                        {autoDismiss && (
                                            <div
                                                className="vnf-toast__progress"
                                                style={{
                                                    animationDuration: `${autoDismissTimeout || 4000}ms`,
                                                }}
                                            >
                                                <CircularProgressbar
                                                    strokeWidth={10}
                                                    value={100}
                                                />
                                            </div>
                                        )}
                                    </button>
                                )}
                            </div>
                            {timestamp && showTime && (
                                <p className="vnf-toast__date">{timeAgo}</p>
                            )}
                            {message && (
                                <div className="vnf-toast__content">
                                    <p className="vnf-toast__message">
                                        {message}
                                    </p>
                                </div>
                            )}
                            {buttons.length > 0 && (
                                <div className="vnf-toast__actions">
                                    {buttons.map((button, index) => (
                                        <button
                                            key={index}
                                            className={`vnf-toast__btn ${button.primary ? "vnf-toast__btn--primary" : "vnf-toast__btn--secondary"}`}
                                            onClick={() => {
                                                button.onClick &&
                                                    button.onClick();
                                                if (button.closeAfterClick) {
                                                    closeToast(id);
                                                }
                                            }}
                                        >
                                            {button.text}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </ToastContext.Provider>
    );
});

/**
 * Custom hook to use toast functionality
 *
 * @returns {Object} Toast functions { addToast, removeToast, updateToast }
 */
export const useToasts = () => useContext(ToastContext);

/**
 * Higher-order component that provides toast functionality to a component
 *
 * @param {React.ComponentType} Component - The component to wrap
 * @returns {React.ReactElement} The wrapped component with toast props
 */
export const withToast = (Component) => {
    return function WrappedComponent(props) {
        const toastFuncs = useToasts();
        return <Component {...props} {...toastFuncs} />;
    };
};
